import { Button, Chip } from '@emailforge/ui';
import { useMemo, useState } from 'react';

import { Icon } from '@/components/ui/icons';
import { useListAuditQuery } from '@/features/audit/auditApi';
import {
  useGetTemplateQuery,
  useGetVersionQuery,
  useListVersionsQuery,
  useRestoreVersionMutation,
} from '@/features/templates/templatesApi';

import { type BlockChange,diffAst } from './astDiff';

export function VersionHistoryDrawer({
  templateId,
  open,
  onClose,
}: {
  templateId: string;
  open: boolean;
  onClose: () => void;
}) {
  const { data, isLoading } = useListVersionsQuery(templateId, { skip: !open });
  const [restore, restoreMut] = useRestoreVersionMutation();
  const [restoredId, setRestoredId] = useState<string | null>(null);
  const [diffVersionId, setDiffVersionId] = useState<string | null>(null);
  const [tab, setTab] = useState<'versions' | 'activity'>('versions');

  if (!open) return null;

  const handleRestore = async (versionId: string, versionNumber: number) => {
    if (
      !window.confirm(
        `Restore v${versionNumber}? A new version will be created from it so history stays linear.`,
      )
    ) {
      return;
    }
    await restore({ templateId, versionId }).unwrap();
    setRestoredId(versionId);
    setTimeout(() => onClose(), 800);
  };

  return (
    <>
      <div
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.25)',
          zIndex: 40,
        }}
      />
      <div
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          bottom: 0,
          width: diffVersionId ? 720 : 420,
          background: 'var(--paper)',
          borderLeft: '1px solid var(--line)',
          boxShadow: '-8px 0 24px rgba(0,0,0,0.08)',
          zIndex: 41,
          display: 'flex',
          flexDirection: 'column',
          transition: 'width 180ms ease',
        }}
      >
        <div
          style={{
            padding: '14px 18px',
            borderBottom: '1px solid var(--line-soft)',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
          }}
        >
          <Icon.history size={15} />
          <div style={{ fontSize: 14, fontWeight: 600 }}>
            {diffVersionId ? 'Compare to current' : tab === 'versions' ? 'Version history' : 'Activity'}
          </div>
          <div style={{ flex: 1 }} />
          {diffVersionId && (
            <Button variant="ghost" size="sm" onClick={() => setDiffVersionId(null)}>
              ← Back to list
            </Button>
          )}
          <Button variant="ghost" size="sm" icon onClick={onClose} aria-label="Close">
            <Icon.x size={12} />
          </Button>
        </div>

        {!diffVersionId && (
          <div style={{ display: 'flex', borderBottom: '1px solid var(--line-soft)', padding: '0 8px' }}>
            {(['versions', 'activity'] as const).map((t) => {
              const active = tab === t;
              return (
                <button
                  key={t}
                  onClick={() => setTab(t)}
                  className="mono"
                  style={{
                    flex: 1,
                    padding: '10px 8px',
                    fontSize: 10.5,
                    textTransform: 'uppercase',
                    letterSpacing: 0.08,
                    color: active ? 'var(--ink)' : 'var(--ink-3)',
                    background: 'transparent',
                    border: 'none',
                    borderBottom: `2px solid ${active ? 'var(--flame)' : 'transparent'}`,
                    cursor: 'pointer',
                    fontWeight: active ? 600 : 500,
                  }}
                >
                  {t}
                </button>
              );
            })}
          </div>
        )}

        <div style={{ flex: 1, overflowY: 'auto', padding: 12 }}>
          {diffVersionId ? (
            <DiffView
              templateId={templateId}
              versionId={diffVersionId}
              onRestore={(num) => {
                const v = data?.find((vv) => vv.id === diffVersionId);
                if (v) handleRestore(v.id, num);
              }}
              restoring={restoreMut.isLoading}
            />
          ) : tab === 'activity' ? (
            <ActivityList templateId={templateId} />
          ) : (
            <>
              {isLoading && <div style={{ fontSize: 13, color: 'var(--ink-3)' }}>Loading…</div>}
              {!isLoading && (data ?? []).length === 0 && (
                <div style={{ fontSize: 13, color: 'var(--ink-3)', textAlign: 'center', padding: 20 }}>
                  No saved versions yet.
                </div>
              )}
              {(data ?? []).map((v, i) => {
                const isLatest = i === 0;
                const isRestored = restoredId === v.id;
                return (
                  <div
                    key={v.id}
                    style={{
                      padding: 12,
                      borderRadius: 'var(--r-2)',
                      border: '1px solid var(--line-soft)',
                      marginBottom: 8,
                      background: isRestored ? 'var(--spark-soft)' : 'var(--paper)',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                      <span
                        className="mono"
                        style={{
                          fontSize: 11,
                          fontWeight: 600,
                          padding: '2px 6px',
                          borderRadius: 4,
                          background: 'var(--paper-3)',
                          color: 'var(--ink-2)',
                        }}
                      >
                        v{v.versionNumber}
                      </span>
                      {isLatest && <Chip style={{ fontSize: 10 }}>latest</Chip>}
                      {v.isPublished && (
                        <Chip variant="flame" style={{ fontSize: 10 }}>
                          published
                        </Chip>
                      )}
                      <div style={{ flex: 1 }} />
                      <span className="mono" style={{ fontSize: 10.5, color: 'var(--ink-3)' }}>
                        {new Date(v.createdAt).toLocaleString()}
                      </span>
                    </div>
                    <div style={{ fontSize: 12.5, color: 'var(--ink-2)', marginBottom: 4 }}>
                      {v.changeSummary ?? <span style={{ color: 'var(--ink-3)' }}>(no summary)</span>}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span className="mono" style={{ fontSize: 10.5, color: 'var(--ink-3)' }}>
                        {v.author?.name ?? v.author?.email ?? 'Unknown'}
                      </span>
                      <div style={{ flex: 1 }} />
                      {!isLatest && (
                        <>
                          <Button variant="ghost" size="sm" onClick={() => setDiffVersionId(v.id)}>
                            Compare
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            disabled={restoreMut.isLoading}
                            onClick={() => handleRestore(v.id, v.versionNumber)}
                          >
                            {isRestored ? 'Restored' : 'Restore'}
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </>
          )}
        </div>
      </div>
    </>
  );
}

/**
 * Side-by-comparison: renders a flat list of block-level changes between the
 * picked version and the template's current latest version. Loads both
 * documents on demand (the list view doesn't carry the AST) and deep-diffs
 * via `diffAst`.
 */
function DiffView({
  templateId,
  versionId,
  onRestore,
  restoring,
}: {
  templateId: string;
  versionId: string;
  onRestore: (versionNumber: number) => void;
  restoring: boolean;
}) {
  const { data: template } = useGetTemplateQuery(templateId);
  const { data: oldVersion, isLoading: loadingOld } = useGetVersionQuery({
    templateId,
    versionId,
  });

  const diff = useMemo(() => {
    if (!template?.latestVersion || !oldVersion) return null;
    return diffAst(oldVersion.astJson, template.latestVersion.astJson);
  }, [template?.latestVersion, oldVersion]);

  if (loadingOld || !template || !oldVersion || !diff) {
    return <div style={{ fontSize: 13, color: 'var(--ink-3)', padding: 20 }}>Loading diff…</div>;
  }

  const { summary, changes } = diff;
  const noChanges = changes.length === 0;

  return (
    <div>
      <div
        style={{
          padding: 12,
          background: 'var(--paper-2)',
          borderRadius: 'var(--r-2)',
          marginBottom: 12,
          fontSize: 12.5,
          lineHeight: 1.5,
        }}
      >
        Comparing{' '}
        <span className="mono" style={{ color: 'var(--ink)' }}>
          v{oldVersion.versionNumber}
        </span>{' '}
        →{' '}
        <span className="mono" style={{ color: 'var(--ink)' }}>
          v{template.latestVersion?.versionNumber ?? '?'} (current)
        </span>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
          <SummaryPill label="Added" count={summary.added} kind="added" />
          <SummaryPill label="Removed" count={summary.removed} kind="removed" />
          <SummaryPill label="Moved" count={summary.moved} kind="moved" />
          <SummaryPill label="Modified" count={summary.modified} kind="modified" />
        </div>
      </div>

      {noChanges && (
        <div style={{ fontSize: 13, color: 'var(--ink-3)', textAlign: 'center', padding: 20 }}>
          No structural changes between these versions.
        </div>
      )}

      {changes.map((c, i) => (
        <ChangeRow key={i} change={c} />
      ))}

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
        <Button
          variant="flame"
          size="sm"
          disabled={restoring}
          onClick={() => onRestore(oldVersion.versionNumber)}
        >
          {restoring ? 'Restoring…' : `Restore v${oldVersion.versionNumber}`}
        </Button>
      </div>
    </div>
  );
}

function SummaryPill({
  label,
  count,
  kind,
}: {
  label: string;
  count: number;
  kind: 'added' | 'removed' | 'moved' | 'modified';
}) {
  if (count === 0) return null;
  const color = colorFor(kind);
  return (
    <span
      className="mono"
      style={{
        fontSize: 10.5,
        padding: '2px 8px',
        borderRadius: 4,
        background: color.bg,
        color: color.fg,
        letterSpacing: 0.04,
      }}
    >
      {count} {label.toLowerCase()}
    </span>
  );
}

function ChangeRow({ change }: { change: BlockChange }) {
  const blockType =
    change.kind === 'modified'
      ? change.after.type
      : change.kind === 'added' || change.kind === 'removed' || change.kind === 'moved'
        ? change.block.type
        : 'unknown';
  const color = colorFor(change.kind);

  let detail: string;
  if (change.kind === 'added') detail = `inserted at position ${change.index + 1}`;
  else if (change.kind === 'removed') detail = `was at position ${change.index + 1}`;
  else if (change.kind === 'moved')
    detail = `position ${change.fromIndex + 1} → ${change.toIndex + 1}`;
  else detail = `props changed: ${change.changedProps.join(', ')}`;

  return (
    <div
      style={{
        padding: '10px 12px',
        border: '1px solid var(--line-soft)',
        borderLeft: `3px solid ${color.fg}`,
        borderRadius: 'var(--r-2)',
        marginBottom: 6,
        background: 'var(--paper)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
        <span
          className="mono"
          style={{
            fontSize: 9.5,
            textTransform: 'uppercase',
            letterSpacing: 0.08,
            color: color.fg,
            fontWeight: 600,
          }}
        >
          {change.kind}
        </span>
        <span style={{ fontSize: 12.5, fontWeight: 550 }}>{blockType}</span>
      </div>
      <div style={{ fontSize: 11.5, color: 'var(--ink-3)', lineHeight: 1.5 }}>{detail}</div>
    </div>
  );
}

function colorFor(kind: BlockChange['kind']): { fg: string; bg: string } {
  switch (kind) {
    case 'added':
      return { fg: 'var(--spark, #2c8a55)', bg: 'rgba(0, 160, 100, 0.10)' };
    case 'removed':
      return { fg: 'var(--err)', bg: 'var(--flame-soft)' };
    case 'moved':
      return { fg: 'rgb(40, 95, 200)', bg: 'rgba(60, 130, 255, 0.10)' };
    case 'modified':
      return { fg: 'var(--flame-2)', bg: 'var(--flame-soft)' };
  }
}

function ActivityList({ templateId }: { templateId: string }) {
  const { data, isLoading } = useListAuditQuery({
    entityType: 'template',
    entityId: templateId,
    limit: 80,
  });

  if (isLoading) {
    return <div style={{ fontSize: 13, color: 'var(--ink-3)' }}>Loading activity…</div>;
  }
  if (!data || data.length === 0) {
    return (
      <div style={{ fontSize: 13, color: 'var(--ink-3)', textAlign: 'center', padding: 20 }}>
        No activity recorded yet.
      </div>
    );
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {data.map((entry) => (
        <div
          key={entry.id}
          style={{
            padding: '10px 12px',
            border: '1px solid var(--line-soft)',
            borderRadius: 'var(--r-2)',
            background: 'var(--paper)',
            fontSize: 12.5,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <span
              className="mono"
              style={{
                fontSize: 10.5,
                padding: '2px 6px',
                borderRadius: 4,
                background: 'var(--paper-3)',
                color: 'var(--ink-2)',
              }}
            >
              {entry.action}
            </span>
            <div style={{ flex: 1 }} />
            <span className="mono" style={{ fontSize: 10.5, color: 'var(--ink-3)' }}>
              {new Date(entry.createdAt).toLocaleString()}
            </span>
          </div>
          <div style={{ color: 'var(--ink-2)' }}>
            {entry.actor?.name ?? entry.actor?.email ?? <span style={{ color: 'var(--ink-3)' }}>System</span>}
          </div>
        </div>
      ))}
    </div>
  );
}
